
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Wallet, RefreshCw, DollarSign, Loader2, CheckCircle, AlertTriangle, BarChart3 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import RefundReconciler from '@/components/admin/tools/RefundReconciler';
import { glassCard, glassCardStatic } from '@/utils/admin-glass-styles';
interface StripeSummary {
  totalConnectedAccounts: number;
  totalPending: number;
  totalActive: number;
  totalRestricted: number;
}

const AdminPayments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stripeSummary, setStripeSummary] = useState<StripeSummary>({
    totalConnectedAccounts: 0,
    totalPending: 0,
    totalActive: 0,
    totalRestricted: 0
  });

  const isOnboardingComplete = searchParams.get('onboarding_complete') === 'true';

  const fetchStripeSummary = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('practitioners')
        .select('payout_details_status, stripe_account_id')
        .not('stripe_account_id', 'is', null);
      
      if (error) {
        console.error('Error fetching Stripe summary:', error);
        return;
      }
      
      // If onboarding is complete via URL, count this as an active account
      const summary = {
        totalConnectedAccounts: data.length,
        totalPending: isOnboardingComplete ? 
          data.filter(p => p.payout_details_status === 'pending' && !isOnboardingComplete).length :
          data.filter(p => p.payout_details_status === 'pending').length,
        totalActive: isOnboardingComplete ? 
          data.filter(p => p.payout_details_status === 'active' || isOnboardingComplete).length :
          data.filter(p => p.payout_details_status === 'active').length,
        totalRestricted: data.filter(p => 
          p.payout_details_status === 'restricted' || 
          p.payout_details_status === 'rejected'
        ).length
      };
      
      setStripeSummary(summary);
    } catch (err) {
      console.error('Unexpected error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStripeSummary();
  }, [isOnboardingComplete]);

  const getCardIcon = (type: string) => {
    if (isOnboardingComplete && type === 'active') {
      return <CheckCircle className="mr-2 h-4 w-4 text-green-600" />;
    }
    switch (type) {
      case 'connected':
        return <Wallet className="mr-2 h-4 w-4 text-indigo-600" />;
      case 'active':
        return <DollarSign className="mr-2 h-4 w-4 text-green-600" />;
      case 'pending':
        return <RefreshCw className="mr-2 h-4 w-4 text-amber-600" />;
      case 'restricted':
        return <AlertTriangle className="mr-2 h-4 w-4 text-red-600" />;
      default:
        return <ExternalLink className="mr-2 h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Payments & Finance</h2>
        <Button 
          variant="outline" 
          size="sm"
          onClick={fetchStripeSummary}
          disabled={isLoading}
        >
          {isLoading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
          ) : (
            <><RefreshCw className="h-4 w-4 mr-2" /> Refresh Data</>
          )}
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className={glassCard}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Connected Accounts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              {getCardIcon('connected')}
              <div className="text-2xl font-semibold">
                {isLoading ? <span className="text-sm">Loading...</span> : stripeSummary.totalConnectedAccounts}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={glassCard}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              {isOnboardingComplete ? 'Connected & Pending Verification' : 'Active Accounts'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              {getCardIcon('active')}
              <div className="text-2xl font-semibold">
                {isLoading ? <span className="text-sm">Loading...</span> : stripeSummary.totalActive}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={glassCard}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Pending Setup</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              {getCardIcon('pending')}
              <div className="text-2xl font-semibold">
                {isLoading ? <span className="text-sm">Loading...</span> : stripeSummary.totalPending}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={glassCard}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Restricted Accounts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              {getCardIcon('restricted')}
              <div className="text-2xl font-semibold">
                {isLoading ? <span className="text-sm">Loading...</span> : stripeSummary.totalRestricted}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <RefundReconciler />
      
      <Card className={glassCardStatic}>
        <CardHeader>
          <CardTitle className="text-lg">Financial Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-6 text-center space-y-4">
            <p className="text-muted-foreground">
              View comprehensive platform financial data, revenue analytics, and payment insights directly in the admin dashboard.
            </p>
            <div className="flex justify-center space-x-4">
              <Button 
                onClick={() => navigate('/admin/finances')}
                className="bg-green-600 hover:bg-green-700"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                View Financial Dashboard
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.open('https://dashboard.stripe.com', '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Stripe Dashboard
              </Button>
            </div>
          </div>
          
          <div className="mt-8 p-4 border rounded-md bg-blue-50">
            <p className="text-blue-800 text-sm">
              <strong>Financial Dashboard:</strong> View real-time platform revenue, commission analysis, and practitioner performance metrics integrated with Stripe API.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPayments;
